import { LEGACY_REDIRECTS, ME_SECTIONS, PASSPORT_SECTIONS, ROUTES,
  type MeSection, type PassportSection } from "@oshimap/config";

/**
 * IA-4: 모든 화면이 고유 URL 을 갖는다. Web 과 Native 가 같은 경로를 해석한다.
 * 라이브러리를 쓰지 않는 이유: 경로 표가 `@oshimap/config` 에 이미 있고,
 * Native 쪽 파서와 규칙을 일치시키려면 한 곳에서 정의하는 편이 안전하다.
 */
export type RouteName =
  | "candidateFeed" | "externalInfo" | "home" | "map" | "explore" | "exploreCategory" | "search"
  | "place" | "events" | "event" | "passport" | "me" | "settings" | "submit" | "visitNew" | "legal" | "notFound";

export interface Route {
  name: RouteName;
  params: Record<string, string>;
  query: URLSearchParams;
  path: string;
}

const TABLE: Array<[RegExp, RouteName, string[]]> = [
  [/^\/admin\/ingestion$/, "candidateFeed", []],
  [/^\/external-info$/, "externalInfo", []],
  [/^\/$/, "home", []],
  [/^\/map$/, "map", []],
  [/^\/explore$/, "explore", []],
  [/^\/explore\/([^/]+)$/, "exploreCategory", ["category"]],
  [/^\/search$/, "search", []],
  [/^\/places\/([^/]+)$/, "place", ["placeId"]],
  [/^\/events$/, "events", []],
  [/^\/events\/([^/]+)$/, "event", ["eventId"]],
  [/^\/passport\/([^/]+)(?:\/.*)?$/, "passport", ["section"]],
  [/^\/passport$/, "passport", []],
  [/^\/me\/settings\/([^/]+)$/, "settings", ["section"]],
  [/^\/me\/settings$/, "settings", []],
  [/^\/me\/(reports)\/([^/]+)$/, "me", ["section", "reportId"]],
  [/^\/me\/([^/]+)(?:\/.*)?$/, "me", ["section"]],
  [/^\/me$/, "me", []],
  [/^\/submit$/, "submit", []],
  [/^\/submit\/place\/([^/]+)$/, "submit", ["placeId"]],
  [/^\/visit\/new$/, "visitNew", []],
  [/^\/legal\/([^/]+)$/, "legal", ["doc"]],
];

export function matchRoute(url: string): Route {
  const [rawPath = "/", rawQuery = ""] = url.split("?");
  let path = rawPath.replace(/\/+$/, "") || "/";
  const redirect = LEGACY_REDIRECTS[path];
  if (redirect) path = redirect;

  for (const [re, name, keys] of TABLE) {
    const m = path.match(re);
    if (!m) continue;
    const params: Record<string, string> = {};
    keys.forEach((k, i) => { const v = m[i + 1]; if (v) params[k] = decodeURIComponent(v); });
    return canonical({ name, params, query: new URLSearchParams(rawQuery), path });
  }
  return { name: "notFound", params: {}, query: new URLSearchParams(rawQuery), path };
}

/**
 * 모르는 하위 섹션을 정규 경로로 되돌립니다.
 *
 * `/passport/nonsense` 는 어디에도 매칭되지 않는 섹션이라 화면은 기본 탭(스탬프 북)을
 * 열지만 주소는 그대로 남습니다. 그러면 주소창과 화면이 서로 다른 말을 합니다 —
 * Legacy 경로를 정규화하는 이유(`LEGACY_REDIRECTS` 주석)와 같은 문제입니다.
 * 여기서 경로를 고치면 `use-route` 의 기존 정규화가 주소창까지 맞춰 줍니다.
 */
function canonical(route: Route): Route {
  if (route.name === "passport") {
    const section = route.params.section;
    if (section && !PASSPORT_SECTIONS.includes(section as PassportSection)) {
      return { ...route, params: {}, path: ROUTES.passport };
    }
  }
  if (route.name === "me") {
    const section = route.params.section;
    if (section && !ME_SECTIONS.includes(section as MeSection)) {
      return { ...route, params: {}, path: ROUTES.me };
    }
  }
  return route;
}

export function currentUrl(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + window.location.search;
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (typeof window === "undefined") return;
  const target = LEGACY_REDIRECTS[to.split("?")[0] ?? to] ?? to;
  if (opts.replace) window.history.replaceState({}, "", target);
  else window.history.pushState({}, "", target);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
